import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MCPActionCreate,
  MCPTool,
  API_METHOD_OPTIONS,
  APIMethod,
  ResponseProjection,
  ProjectionField,
  PROJECTION_MODE_OPTIONS,
  NormalizedParameters,
  GraphQLConfig,
  RequestConfig,
  ParameterConfig,
} from '@/types/mcpTool';
import { MCPToolsService } from '@/api/services/mcpToolsService';
import { Loader2, CheckCircle2, Plus, Trash2, Upload, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { MethodAwareRequestBuilder } from './smart-forms/MethodAwareRequestBuilderNew';
import { ActionTestSimulator } from './smart-forms/ActionTestSimulator';

interface CreateMCPActionDialogProps {
  tool: MCPTool | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  mode?: 'create' | 'edit';
  initialAction?: MCPAction | null;
}

export const CreateMCPActionDialogNew: React.FC<CreateMCPActionDialogProps> = ({
  tool,
  open,
  onOpenChange,
  onSuccess,
  mode = 'create',
  initialAction = null,
}) => {
  const [currentTab, setCurrentTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Basic Info
  const [actionName, setActionName] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [apiMethod, setApiMethod] = useState<APIMethod>('GET');

  // Request Configuration
  const [normalizedParams, setNormalizedParams] = useState<NormalizedParameters>({
    path_params: {},
    query_params: {},
    body: null,
  });
  const [pathParamConfigs, setPathParamConfigs] = useState<Record<string, ParameterConfig>>({});
  const [queryParamConfigs, setQueryParamConfigs] = useState<Record<string, ParameterConfig>>({});
  const [bodyParamConfigs, setBodyParamConfigs] = useState<Record<string, ParameterConfig>>({});
  const [graphqlConfig, setGraphQLConfig] = useState<GraphQLConfig | undefined>();
  const [requestConfig, setRequestConfig] = useState<RequestConfig | undefined>();

  // Response Handling
  const [exampleResponseJson, setExampleResponseJson] = useState('');
  const [projectionMode, setProjectionMode] = useState<'singular' | 'list'>('singular');
  const [itemsPath, setItemsPath] = useState('');
  const [fields, setFields] = useState<ProjectionField[]>([]);

  const resetForm = () => {
    setCurrentTab('basic');
    setActionName('');
    setEndpoint('');
    setApiMethod('GET');
    setNormalizedParams({ path_params: {}, query_params: {}, body: null });
    setPathParamConfigs({});
    setQueryParamConfigs({});
    setBodyParamConfigs({});
    setGraphQLConfig(undefined);
    setRequestConfig(undefined);
    setExampleResponseJson('');
    setProjectionMode('singular');
    setItemsPath('');
    setFields([]);
    setError(null);
  };

  // Pre-populate form when in edit mode
  useEffect(() => {
    if (mode === 'edit' && initialAction && open) {
      console.log('🔧 Edit mode - Pre-populating form with action:', initialAction);

      setActionName(initialAction.action_name);
      setEndpoint(initialAction.endpoint);

      // Smart detection: If graphql_config exists, this is a GraphQL action
      const detectedMethod = initialAction.graphql_config
        ? 'GRAPHQL'
        : (initialAction.api_method || initialAction.http_method);
      setApiMethod(detectedMethod);
      console.log('🔍 Detected method:', detectedMethod, 'has graphql_config:', !!initialAction.graphql_config);

      // Populate parameters - convert from JSON schema back to normalized format
      if (initialAction.parameters) {
        const requiredFields = new Set<string>(initialAction.parameters.required || []);
        const properties = initialAction.parameters.properties || {};
        const endpointForParsing = initialAction.endpoint || endpoint || '';
        const pathKeysFromEndpoint = Array.from(
          endpointForParsing.matchAll(/\{([^}]+)\}/g)
        ).map((match) => match[1]);

        const resolvedPathParams: Record<string, any> = {};
        const resolvedQueryParams: Record<string, any> = {};
        const resolvedBodyFields: Record<string, any> = {};

        const existingPathConfigs = { ...(initialAction.param_configs?.path || {}) };
        const existingQueryConfigs = { ...(initialAction.param_configs?.query || {}) };
        const existingBodyConfigs = { ...(initialAction.param_configs?.body || {}) };

        const makeDefaultConfig = (key: string): ParameterConfig => ({
          source: 'direct_input',
          fallback: 'error',
          required: requiredFields.has(key),
          let_ai_decide: false,
          handoff_to_human: false,
        });

        const defaultValueForType = (type: string): any => '';

        Object.entries(properties).forEach(([key, schema]) => {
          const description = typeof schema?.description === 'string'
            ? schema.description.toLowerCase()
            : '';
          const schemaType = typeof schema?.type === 'string' ? schema.type : 'string';

          const isGraphQL = detectedMethod === 'GRAPHQL';
          const isPathParam =
            description.includes('path parameter') ||
            pathKeysFromEndpoint.includes(key);
          const isQueryParam = description.includes('query parameter');
          const isBodyField =
            description.includes('body field') ||
            (!isPathParam && !isQueryParam && detectedMethod !== 'GET');

          if (isGraphQL) {
            resolvedPathParams[key] = '';
            if (!existingPathConfigs[key]) {
              existingPathConfigs[key] = makeDefaultConfig(key);
            } else if (requiredFields.has(key)) {
              existingPathConfigs[key] = {
                ...existingPathConfigs[key],
                required: true,
              };
            }
            return;
          }

          if (isPathParam) {
            resolvedPathParams[key] = '';
            if (!existingPathConfigs[key]) {
              existingPathConfigs[key] = makeDefaultConfig(key);
            } else if (requiredFields.has(key)) {
              existingPathConfigs[key] = {
                ...existingPathConfigs[key],
                required: true,
              };
            }
            return;
          }

          if (isQueryParam || detectedMethod === 'GET') {
            resolvedQueryParams[key] = defaultValueForType(schemaType);
            if (!existingQueryConfigs[key]) {
              existingQueryConfigs[key] = makeDefaultConfig(key);
            } else if (requiredFields.has(key)) {
              existingQueryConfigs[key] = {
                ...existingQueryConfigs[key],
                required: true,
              };
            }
            return;
          }

          // Default to body parameter for POST/PUT/PATCH
          resolvedBodyFields[key] = defaultValueForType(schemaType);
          if (!existingBodyConfigs[key]) {
            existingBodyConfigs[key] = makeDefaultConfig(key);
          } else if (requiredFields.has(key)) {
            existingBodyConfigs[key] = {
              ...existingBodyConfigs[key],
              required: true,
            };
          }
        });

        const includeBody =
          detectedMethod === 'POST' ||
          detectedMethod === 'PUT' ||
          detectedMethod === 'PATCH';

        const normalized: NormalizedParameters = {
          path_params: resolvedPathParams,
          query_params: resolvedQueryParams,
          body: includeBody
            ? (Object.keys(resolvedBodyFields).length > 0 ? resolvedBodyFields : {})
            : null,
        };

        setNormalizedParams(normalized);
        setPathParamConfigs(existingPathConfigs);
        setQueryParamConfigs(existingQueryConfigs);
        setBodyParamConfigs(existingBodyConfigs);
      }

      // Populate example response
      if (initialAction.example_response) {
        setExampleResponseJson(JSON.stringify(initialAction.example_response, null, 2));
      }

      // Populate response projection
      if (initialAction.response_projection) {
        setProjectionMode(initialAction.response_projection.mode);
        setItemsPath(initialAction.response_projection.items_path || '');
        setFields(initialAction.response_projection.fields || []);
      }

      // Populate GraphQL config
      if (initialAction.graphql_config) {
        console.log('📊 Setting GraphQL config:', initialAction.graphql_config);
        setGraphQLConfig(initialAction.graphql_config);
      } else {
        console.log('⚠️ No GraphQL config found in initialAction');
      }

      // Populate request config
      if (initialAction.request_config) {
        console.log('⚙️ Setting request config:', initialAction.request_config);
        setRequestConfig(initialAction.request_config);
      }

      console.log('✅ Form pre-population complete');
    }
  }, [mode, initialAction, open]);

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setExampleResponseJson(content);
    };
    reader.readAsText(file);
  };

  const addField = () => {
    setFields([...fields, { path: '', label: '' }]);
  };

  const updateField = (index: number, key: 'path' | 'label', value: string) => {
    const newFields = [...fields];
    newFields[index][key] = value;
    setFields(newFields);
  };

  const removeField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const canProceedToNextTab = () => {
    switch (currentTab) {
      case 'basic':
        return actionName.trim() && endpoint.trim();
      case 'request':
        // Always can proceed - parameters are optional
        return true;
      case 'response':
        return true;
      case 'test':
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!tool) return;

    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!actionName.trim()) {
        throw new Error('Action name is required');
      }
      if (!endpoint.trim()) {
        throw new Error('Endpoint is required');
      }

      // Validate projection if mode is list
      if (projectionMode === 'list' && !itemsPath.trim()) {
        throw new Error('Items path is required for list projection mode');
      }

      // Parse example response
      let parsedExampleResponse;
      if (exampleResponseJson.trim()) {
        try {
          parsedExampleResponse = JSON.parse(exampleResponseJson);
        } catch (e) {
          throw new Error('Invalid JSON in example response');
        }
      }

      // Build response projection
      const responseProjection: ResponseProjection = {
        mode: projectionMode,
        fields: fields.filter(f => f.path && f.label),
      };

      if (projectionMode === 'list') {
        responseProjection.items_path = itemsPath;
      }

      // Build parameters from normalized data
      const parameters = {
        type: 'object',
        properties: {},
        required: [] as string[],
      };

      // Add path parameters
      Object.entries(normalizedParams.path_params).forEach(([key, value]) => {
        parameters.properties[key] = {
          type: 'string',
          description: `Path parameter: {${key}}`,
        };
        parameters.required.push(key);
      });

      // Add query/body parameters
      if (apiMethod === 'GET') {
        Object.entries(normalizedParams.query_params).forEach(([key, value]) => {
          parameters.properties[key] = {
            type: typeof value === 'number' ? 'number' : 'string',
            description: `Query parameter: ${key}`,
          };
        });
      } else if (normalizedParams.body) {
        Object.entries(normalizedParams.body).forEach(([key, value]) => {
          parameters.properties[key] = {
            type: typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'string',
            description: `Body field: ${key}`,
          };
        });
      }

      const actionData: MCPActionCreate = {
        param_configs: {path:pathParamConfigs,query:queryParamConfigs,body:bodyParamConfigs},
        action_name: actionName.trim(),
        endpoint: endpoint.trim(),
        http_method: apiMethod === 'GRAPHQL' ? 'POST' : apiMethod,
        api_method: apiMethod,
        parameters: Object.keys(parameters.properties).length > 0 ? parameters : undefined,
        example_response: parsedExampleResponse,
        response_projection: responseProjection.fields.length > 0 ? responseProjection : undefined,
        graphql_config: apiMethod === 'GRAPHQL' ? graphqlConfig : undefined,
        request_config: requestConfig,
      };

      if (mode === 'edit' && initialAction) {
        await MCPToolsService.updateMCPAction(tool.id!, initialAction.action_name, actionData);
      } else {
        await MCPToolsService.createMCPAction(tool.id!, actionData);
      }
      onSuccess?.();
      handleClose();
    } catch (err: any) {
      console.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} MCP action:`, err);
      setError(err.message || `Failed to ${mode === 'edit' ? 'update' : 'create'} action`);
    } finally {
      setLoading(false);
    }
  };

  if (!tool) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'edit' ? 'Edit' : 'Create'} Action for {tool.name}
            {apiMethod && (
              <Badge variant="secondary" className="text-xs">
                {apiMethod}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {mode === 'edit'
              ? 'Update the API endpoint action configuration'
              : 'Configure a new API endpoint action with live testing'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">1. Basic Info</TabsTrigger>
            <TabsTrigger value="request" disabled={!canProceedToNextTab()}>
              2. Request
            </TabsTrigger>
            <TabsTrigger value="response" disabled={currentTab === 'basic'}>
              3. Response
            </TabsTrigger>
            <TabsTrigger value="test" disabled={currentTab === 'basic'}>
              4. Test
            </TabsTrigger>
            <TabsTrigger value="review" disabled={currentTab === 'basic'}>
              5. Review
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: Basic Info */}
          <TabsContent value="basic" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="actionName">
                Action Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="actionName"
                disabled={mode === 'edit'}
                placeholder="get_customer"
                value={actionName}
                onChange={(e) => setActionName(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Unique identifier for this action (use lowercase, underscores)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="endpoint">
                Endpoint Path <span className="text-red-500">*</span>
              </Label>
              <Input
                id="endpoint"
                placeholder="/v1/customers/{customer_id}"
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Relative path from base URL ({tool.base_url}). Use {'{placeholders}'} for dynamic values
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="apiMethod">
                API Method <span className="text-red-500">*</span>
              </Label>
              <Select value={apiMethod} onValueChange={(v) => setApiMethod(v as APIMethod)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {API_METHOD_OPTIONS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Select HTTP method or GraphQL for GraphQL APIs
              </p>
            </div>

            {apiMethod && (
              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-900 text-sm">
                  <strong>{apiMethod} Request:</strong>{' '}
                  {apiMethod === 'GET' && 'Query parameters will be added to the URL'}
                  {apiMethod === 'POST' && 'Supports request body with multiple content types'}
                  {apiMethod === 'PUT' && 'Updates resource with request body'}
                  {apiMethod === 'PATCH' && 'Partially updates resource with request body'}
                  {apiMethod === 'DELETE' && 'Deletes resource, typically no body'}
                  {apiMethod === 'GRAPHQL' && 'GraphQL query/mutation with variables'}
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          {/* TAB 2: Request Configuration */}
          <TabsContent value="request" className="space-y-4">
            <MethodAwareRequestBuilder
              tool={tool}
              endpoint={endpoint}
              method={apiMethod}
              onValuesChange={(params, pathConfigs, queryConfigs, bodyConfigs, gqlConfig, reqConfig) => {
                setNormalizedParams(params);
                setPathParamConfigs(pathConfigs);
                setQueryParamConfigs(queryConfigs);
                setBodyParamConfigs(bodyConfigs);
                setGraphQLConfig(gqlConfig);
                setRequestConfig(reqConfig);
              }}
              onEndpointChange={setEndpoint}
              initialValues={normalizedParams}
              initialPathConfigs={pathParamConfigs}
              initialQueryConfigs={queryParamConfigs}
              initialBodyConfigs={bodyParamConfigs}
              initialGraphQLConfig={graphqlConfig}
              initialRequestConfig={requestConfig}
            />
          </TabsContent>

          {/* TAB 3: Response Handling */}
          <TabsContent value="response" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="exampleResponse">Example API Response (Optional)</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="response-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('response-upload')?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload JSON
                </Button>
              </div>
              <Textarea
                id="exampleResponse"
                placeholder='{"id": "cus_123", "email": "user@example.com", "status": "active"}'
                value={exampleResponseJson}
                onChange={(e) => setExampleResponseJson(e.target.value)}
                rows={12}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                Sample response from the API (used for field selection)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Response Type</Label>
              <Select value={projectionMode} onValueChange={(v) => setProjectionMode(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROJECTION_MODE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-xs text-gray-500">{option.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {projectionMode === 'list' && (
              <div className="space-y-2">
                <Label htmlFor="itemsPath">
                  Items Path <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="itemsPath"
                  placeholder="$.data.results"
                  value={itemsPath}
                  onChange={(e) => setItemsPath(e.target.value)}
                />
                <p className="text-xs text-gray-500">
                  JSONPath to the array of items (e.g., $.data, $.results)
                </p>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Fields to Extract</Label>
                <Button type="button" variant="outline" size="sm" onClick={addField}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Field
                </Button>
              </div>
              {fields.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">
                  No fields configured. Add fields to extract from the response.
                </p>
              ) : (
                <div className="space-y-2">
                  {fields.map((field, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <div className="flex-1">
                        <Input
                          placeholder="JSONPath (e.g., $.email)"
                          value={field.path}
                          onChange={(e) => updateField(index, 'path', e.target.value)}
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          placeholder="Label (e.g., Email)"
                          value={field.label}
                          onChange={(e) => updateField(index, 'label', e.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeField(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500">
                Define which fields to extract and display from the API response
              </p>
            </div>
          </TabsContent>

          {/* TAB 4: Test & Simulate */}
          <TabsContent value="test" className="space-y-4">
            <ActionTestSimulator
              tool={tool}
              actionName={actionName}
              endpoint={endpoint}
              method={apiMethod}
              parameters={normalizedParams}
              graphqlConfig={graphqlConfig}
              requestConfig={requestConfig}
            />
          </TabsContent>

          {/* TAB 5: Review */}
          <TabsContent value="review" className="space-y-4">
            <Card className="p-4">
              <h3 className="text-lg font-semibold mb-4">Review Your Action</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">Action Name</p>
                  <p className="font-medium">{actionName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Full Endpoint</p>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                    {tool.base_url}{endpoint}
                  </code>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Method</p>
                  <Badge variant="secondary">{apiMethod}</Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Parameters</p>
                  <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                    {JSON.stringify(normalizedParams, null, 2)}
                  </pre>
                </div>
                {graphqlConfig && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">GraphQL Configuration</p>
                    <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                      {JSON.stringify(graphqlConfig, null, 2)}
                    </pre>
                  </div>
                )}
                {fields.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Response Projection</p>
                    <p className="text-sm">
                      Mode: <strong>{projectionMode}</strong>
                    </p>
                    <ul className="list-disc list-inside text-sm mt-2">
                      {fields.map((field, index) => (
                        <li key={index}>
                          {field.label}: <code className="text-xs bg-gray-100 px-1 rounded">{field.path}</code>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>

          {currentTab !== 'review' && (
            <Button
              type="button"
              onClick={() => {
                const tabs = ['basic', 'request', 'response', 'test', 'review'];
                const currentIndex = tabs.indexOf(currentTab);
                if (currentIndex < tabs.length - 1) {
                  setCurrentTab(tabs[currentIndex + 1]);
                }
              }}
              disabled={!canProceedToNextTab()}
            >
              Next
            </Button>
          )}

          {currentTab === 'review' && (
            <Button type="button" onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'edit' ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {mode === 'edit' ? 'Update Action' : 'Create Action'}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
